import type { Footer, MenuItem, Social } from '@nlo/nuxt-common/components/common/common-header/common-header-param-types';
import type { Link } from '@nlo/nuxt-common/types/navigation';
import type { components } from '~/generated/portal-api';

export class PortalHeaderApi {
	baseUrl: string;

	constructor(baseUrl: string) {
		this.baseUrl = baseUrl;
	}

	async getPortalHeaderData() {
		const data = await $fetch<components['schemas']['PortalHeaderViewModel']>(`${this.baseUrl}/api/portal-header`, {
			onResponse({ response }) {
				if (response.status >= 400) {
					throw new NloApiError(response.status, response.statusText);
				}
			},
		});

		const result: { navigationItems: MenuItem[]; footer: Footer } | undefined = {
			footer: {
				...data.footer,
				copyrightText: data.footer.copyrightText || '',
				socialMenu:
					data.footer.socialMenu?.map((item) => ({
						name: item.name as Social,
						link: {
							url: item.href || '',
							target: item.target,
						},
					})) || [],
				nloLinkList: data.footer.nloLinkList?.filter(notEmpty).map((item) => ({
					text: item.title || '',
					url: item.href || '',
					target: item.target,
				})),
				linkList:
					data.footer.linkList?.map((item) => ({
						text: item.title || '',
						url: item.href || '',
						target: item.target,
					})) || [],
			},
			navigationItems: mapHeaderNavigationItems(data.navigationItems) || [],
		};

		return result;
	}
}

/**
 * Maps navigation items from the API to something that the portal-header component understands.
 * The reason this exists is due to the fact that the C# application does not support optionals properly yet.
 * @param items
 */
function mapHeaderNavigationItems(items: components['schemas']['PortalHeaderViewModel']['navigationItems']): MenuItem[] | undefined {
	return items
		?.filter((item) => !!item.title)
		.map((item): MenuItem | undefined => {
			switch (item?._type) {
				case 'HeaderNavigationLinkViewModel':
					return {
						type: 'link',
						link: {
							text: item.title || '',
							url: item.href || '',
						},
					};
				case 'HeaderNavigationSubmenuViewModel': {
					const mapHeaderNavigationItems = item.dropdownLinks
						?.map((item): Link | undefined => {
							if (item._type === 'HeaderNavigationSubmenuViewModel') {
								return undefined;
							}

							return {
								text: item.title || '',
								url: item.href || '',
							};
						})
						.filter(notEmpty);

					return {
						type: 'dropdown',
						title: item.title || '',
						dropdownLinks: mapHeaderNavigationItems || [],
					};
				}
				default:
					return undefined;
			}
		})
		.filter(notEmpty);
}
